import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api-client.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { DataTable, type Column } from '../components/ui/data-table.js';
import { EmptyState } from '../components/ui/empty-state.js';
import { Input } from '../components/ui/input.js';

interface ContactProfile {
  id: string;
  name: string;
  contact_name?: string;
  organization_name?: string;
  direct_number?: string;
  reply_to_email?: string;
  created_at: string;
}

interface ListResponse {
  data: ContactProfile[];
  pagination: { next_cursor: string | null; has_more: boolean };
}

export function ContactProfilesPage(): JSX.Element {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['contact-profiles'],
    queryFn: () => api.get('/v1/contact-profiles') as Promise<ListResponse>,
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    if (!s) return data.data;
    return data.data.filter((row) =>
      [row.name, row.contact_name, row.organization_name, row.direct_number, row.reply_to_email]
        .some((v) => v && v.toLowerCase().includes(s)),
    );
  }, [data, search]);

  const deleteOne = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/contact-profiles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-profiles'] }),
  });

  const columns: Column<ContactProfile>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => <span className="font-medium">{row.name}</span>,
    },
    { key: 'org', header: 'Organization', render: (row) => row.organization_name ?? '—' },
    {
      key: 'phone',
      header: 'Caller ID',
      render: (row) => (row.direct_number ? <code className="text-xs">{row.direct_number}</code> : '—'),
    },
    { key: 'email', header: 'Reply-to', render: (row) => row.reply_to_email ?? '—' },
    {
      key: 'created',
      header: 'Created',
      render: (row) => new Date(row.created_at).toLocaleDateString(),
      width: '120px',
    },
    {
      key: 'actions',
      header: '',
      width: '160px',
      className: 'text-right',
      render: (row) => (
        <div className="flex justify-end gap-1">
          <Link to={`/contact-profiles/${row.id}/edit`}>
            <Button variant="ghost" size="sm">Edit</Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (window.confirm(`Delete contact profile "${row.name}"?`)) deleteOne.mutate(row.id);
            }}
            disabled={deleteOne.isPending}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Contact profiles</h1>
          <p className="text-sm text-ink-muted">
            Outbound caller-ID + per-contact configuration. Used by events and campaigns as the
            "from" identity.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/contact-profiles/import">
            <Button variant="secondary">Import CSV</Button>
          </Link>
          <Link to="/contact-profiles/new">
            <Button>New profile</Button>
          </Link>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>All profiles</CardTitle>
          {data && (
            <span className="text-xs text-ink-muted">
              {search ? `${filtered.length} of ${data.data.length}` : data.data.length}
              {data.pagination.has_more && !search ? '+' : ''}
            </span>
          )}
        </CardHeader>
        <CardBody className="p-0">
          {data && (
            <div className="flex gap-2 px-4 py-3 border-b border-border">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, organization, phone, email…"
                className="flex-1"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="text-sm text-ink-muted hover:text-ink px-2"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          {isLoading && <p className="p-4 text-sm text-ink-muted">Loading…</p>}
          {error && <ErrorBanner error={error} />}
          {data && (
            <DataTable
              data={filtered}
              columns={columns}
              rowKey={(r) => r.id}
              emptyState={
                search ? (
                  <EmptyState
                    title="No matches"
                    description={`Nothing matches "${search}". Try a different term or clear the search.`}
                  />
                ) : (
                  <EmptyState
                    title="No contact profiles yet"
                    description="Create one to use it as the caller-ID on events and campaigns."
                  />
                )
              }
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="m-4 rounded border border-danger bg-surface p-3 text-sm">
      <p className="font-medium text-danger">Failed to load</p>
      <p className="mt-1 text-ink-muted">
        {status ? `HTTP ${status}` : 'Network error'} — {(error as Error).message}
      </p>
    </div>
  );
}
